import * as React from "react";
import { cn } from "@/lib/utils";

// The small status primitives MUI had and shadcn leaves to you: a spinner, a progress
// bar, a chip and a rule. Kept together because each is a few lines.

interface SpinnerProps extends React.ComponentProps<"svg"> {
  /** Diameter in px. MUI's CircularProgress default was 40; the pane mostly uses 16-22. */
  size?: number;
}

/** MUI's CircularProgress (indeterminate). Takes the current text colour. */
const Spinner = ({ size = 20, className, ...props }: SpinnerProps) => (
  <svg
    role="progressbar"
    aria-label="Loading"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className={cn("shrink-0 animate-spin text-primary", className)}
    {...props}
  >
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
    <path
      d="M21.5 12a9.5 9.5 0 0 0-9.5-9.5"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

interface ProgressProps extends React.ComponentProps<"div"> {
  /** 0-100. Omit for indeterminate. */
  value?: number;
}

/** MUI's LinearProgress: a 4px pill. Indeterminate slides a third-width bar along it. */
const Progress = ({ value, className, ...props }: ProgressProps) => (
  <div
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={value}
    className={cn("relative h-1 w-full overflow-hidden rounded-full bg-primary-tint", className)}
    {...props}
  >
    {value === undefined ? (
      <div className="absolute inset-y-0 w-1/3 animate-[progress-slide_1.4s_ease-in-out_infinite] rounded-full bg-primary" />
    ) : (
      <div
        className="h-full rounded-full bg-primary transition-[width]"
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    )}
  </div>
);

type BadgeTone = "default" | "primary" | "error" | "warning" | "success";

const BADGE_TONES: Record<BadgeTone, string> = {
  default: "bg-grey-100 text-foreground",
  primary: "bg-primary-tint text-primary-dark dark:text-primary-light",
  error: "bg-error-tint text-error-dark dark:text-error-light",
  warning: "bg-warning-tint text-warning-dark dark:text-warning-light",
  success: "bg-success-tint text-success-dark dark:text-success-light",
};

interface BadgeProps extends React.ComponentProps<"span"> {
  tone?: BadgeTone;
  /** A bordered chip on no fill: MUI's `variant="outlined"`. */
  outlined?: boolean;
}

/** MUI's small Chip: a 22px pill with a label and an optional leading icon. */
const Badge = ({ tone = "default", outlined = false, className, ...props }: BadgeProps) => (
  <span
    data-slot="badge"
    className={cn(
      "inline-flex h-[22px] max-w-full shrink-0 items-center gap-1 rounded-full px-2 text-[11.5px] font-medium whitespace-nowrap [&_svg]:size-3.5 [&_svg]:shrink-0",
      outlined ? "border border-input bg-transparent text-foreground" : BADGE_TONES[tone],
      className
    )}
    {...props}
  />
);

/** MUI's Divider. */
const Separator = ({
  orientation = "horizontal",
  className,
  ...props
}: React.ComponentProps<"div"> & { orientation?: "horizontal" | "vertical" }) => (
  <div
    role="separator"
    aria-orientation={orientation}
    className={cn(
      "shrink-0 bg-border",
      orientation === "horizontal" ? "h-px w-full" : "w-px self-stretch",
      className
    )}
    {...props}
  />
);

export { Spinner, Progress, Badge, Separator };
export type { BadgeTone };
